import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = 'https://calibraai-backend.fly.dev/api/v1';

const STATUS_LABEL = {
  calibrado: 'Calibrado',
  a_vencer: 'A vencer',
  vencido: 'Vencido',
  bloqueado: 'Bloqueado',
  em_calibracao: 'Em calibração',
  aguardando_laudo: 'Aguardando laudo',
  nao_calibrado: 'Não calibrado',
};

const STATUS_CLASS = {
  calibrado: 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50',
  a_vencer: 'bg-amber-900/60 text-amber-300 border-amber-700/50',
  vencido: 'bg-red-900/60 text-red-300 border-red-700/50',
  bloqueado: 'bg-slate-700/60 text-slate-300 border-slate-600/50',
  em_calibracao: 'bg-purple-900/60 text-purple-300 border-purple-700/50',
  aguardando_laudo: 'bg-orange-900/60 text-orange-300 border-orange-700/50',
  nao_calibrado: 'bg-gray-700/60 text-gray-400 border-gray-600/50',
};

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-200">{value}</p>
    </div>
  );
}

export default function InstrumentPublicPage() {
  const { id } = useParams();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/public/instruments/${id}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error)))
      .then(data => setInstrument(data))
      .catch(err => setError(err || 'Instrumento não encontrado'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 text-sm">
        {error}
      </div>
    );
  }

  const ultima = instrument.calibrations?.[0];
  const statusKey = instrument.calibrationStatus || 'nao_calibrado';
  const empresa = instrument.tenant?.nomeEmpresa || 'CalibraAI';

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur px-6 py-4 flex items-center justify-between">
        {instrument.tenant?.logoUrl ? (
          <img src={instrument.tenant.logoUrl} alt={empresa} className="h-8 object-contain" />
        ) : (
          <span className="text-sm font-semibold text-slate-400">{empresa}</span>
        )}
        <span className="text-[10px] text-slate-600 uppercase tracking-widest">CalibraAI</span>
      </div>

      {/* Conteúdo */}
      <div className="max-w-lg mx-auto px-6 py-8 space-y-6">

        {/* TAG + Descrição + Status */}
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-black font-mono tracking-widest text-white">{instrument.tag}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_CLASS[statusKey] || STATUS_CLASS.nao_calibrado}`}>
              {STATUS_LABEL[statusKey] || statusKey}
            </span>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed">{instrument.description}</p>
        </div>

        {/* Dados gerais */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 grid grid-cols-2 gap-4">
          <Field label="Setor" value={instrument.sector} />
          <Field label="Classe" value={instrument.instrumentClass?.name} />
          <Field label="Marca" value={instrument.marca} />
          <Field label="Modelo" value={instrument.modelo} />
          {instrument.numeroDeSerie && <Field label="N° de Série" value={instrument.numeroDeSerie} />}
        </div>

        {/* Datas de calibração */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Última Calibração</p>
            <p className="text-sm font-medium text-slate-200">
              {instrument.lastCalibrationDate
                ? new Date(instrument.lastCalibrationDate).toLocaleDateString('pt-BR')
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Próxima Calibração</p>
            <p className={`text-sm font-medium ${
              instrument.nextCalibrationDate && new Date(instrument.nextCalibrationDate) < new Date()
                ? 'text-red-400'
                : 'text-slate-200'
            }`}>
              {instrument.nextCalibrationDate
                ? new Date(instrument.nextCalibrationDate).toLocaleDateString('pt-BR')
                : '—'}
            </p>
          </div>
        </div>

        {/* Última calibração concluída */}
        {ultima && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Último Certificado</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data" value={new Date(ultima.data).toLocaleDateString('pt-BR')} />
              <Field label="Resultado" value={ultima.resultado} />
              {ultima.fornecedor && <Field label="Laboratório" value={ultima.fornecedor} />}
              {ultima.numeroCertificado && <Field label="N° Certificado" value={ultima.numeroCertificado} />}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-[11px] text-slate-700 pb-4">
          Informações fornecidas por {empresa} via CalibraAI
        </p>
      </div>
    </div>
  );
}
